import { useEffect, useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { IChart } from "../../../../../@types/chart.type";
import iconDelete from "./../../../../../assets/_TrashIcon_gray.svg";
import { storageUserGet } from "../../../../../storage/storageUser";
import { getCompanyCapacity } from "../../../../../services/api/widgets/widgetsService";
import { IApiResponse } from "../../../../../@types/api-response.type";
import { ICompanyCapacity } from "../../../../../@types/widgets.type";
import { IconCollaborators } from "../../../../../icons/IconCollaborators";
import styles from "./styles.module.scss";

interface CompanyCapacityProps {
  chart: IChart;
  ismodeEdit: boolean;
  deleteChart: (id: string | undefined, widgetId: string) => void;
  rowHeightGrid: number;
}

export function CompanyCapacity({
  chart,
  deleteChart,
  ismodeEdit,
  rowHeightGrid,
}: CompanyCapacityProps) {
  const [data, setData] = useState({} as ICompanyCapacity);
  const [loading, setLoading] = useState(true);

  const user = storageUserGet();

  useEffect(() => {
    async function fetchCompanyCapacity() {
      try {
        setLoading(true);
        const response = (await getCompanyCapacity(
          user.email,
          user?.resourceId as string
        )) as IApiResponse<ICompanyCapacity>;

        if (response.statusCode === 200) {
          setData(response.result);
        }
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    }

    fetchCompanyCapacity();
  }, [user.email, user?.resourceId]);

  const calculatePercentage = useMemo(() => {
    return data?.usageCapacityMonthlies?.map((usageCapacity) => {
      return data.capacityTotal > 0
        ? Number(
            (
              (usageCapacity.usageHours / usageCapacity.availableHours) *
              100
            ).toFixed(0)
          )
        : 0;
    });
  }, [data]);

  const getMonthAbbreviation = (monthNumber: number) => {
    return new Intl.DateTimeFormat("pt-BR", { month: "short" })
      .format(new Date(2022, monthNumber - 1))
      .replace(".", "")
      .toUpperCase();
  };

  const mapedMonthAndYear = useMemo(() => {
    return data?.usageCapacityMonthlies?.map(({ month, year }) => {
      return `${getMonthAbbreviation(month)}/${year}`;
    });
  }, [data]);

  const series = [
    {
      name: "Capacity",
      data: calculatePercentage,
    },
  ];

  const options = {
    chart: {
      type: "area",
      width: "100%",
      height: { rowHeightGrid },
      zoom: {
        enabled: false,
      },
      toolbar: {
        show: false,
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: "smooth",
    },

    labels: mapedMonthAndYear,
    xaxis: {
      type: "date",
      tooltip: {
        enabled: false,
      },
      labels: {
        style: {
          fontSize: "12px",
          fontFamily: "Archivo, sans-serif",
          fontWeight: 500,
          colors: ["#616161"],
        },
      },
    },
    yaxis: {
      min: 0,
      labels: {
        formatter: (value: number) => `${value}%`,
        style: {
          fontSize: "12px",
          fontFamily: "Archivo, sans-serif",
          fontWeight: 500,
          colors: ["#616161"],
        },
      },
    },
    tooltip: {
      custom: function ({ dataPointIndex }: { dataPointIndex: number }) {
        const { month, usageHours, year, availableHours } =
          data.usageCapacityMonthlies[dataPointIndex];

        return `
          <div class='labelCustom'>
            <span><b>${getMonthAbbreviation(month)}/${year}</b></span>
            <span>Capacidade total: 100%</span>
            <span class='blueColor' >Utilização: ${Number(
              ((usageHours / availableHours) * 100).toFixed(0)
            )}%</span>
           
          </div>
        `;
      },
    },
    legend: {
      horizontalAlign: "left",
    },
  };

  return (
    <div className={styles.widget}>
      <div className={styles.header}>
        <h3>
          <IconCollaborators />
          Capacity
        </h3>

        <div>
          <span>Média de utilização</span>
          <b>{data?.usagePercentage?.toFixed(0)}%</b>
        </div>
      </div>
      <div style={{ width: "100%", height: "100%" }}>
        {loading ? (
          <p style={{ textAlign: "center", color: "#212121" }}>Carregando...</p>
        ) : (
          <>
            {data?.usageCapacityMonthlies?.length ? (
              <ReactApexChart
                options={options}
                series={series}
                type="area"
                width="100%"
                height={rowHeightGrid - 56}
              />
            ) : (
              <div
                style={{
                  textAlign: "center",
                  padding: "2rem",
                  color: "#616161",
                }}
              >
                <p style={{ marginTop: "1rem" }}>Nenhum dado encontrado.</p>
              </div>
            )}
          </>
        )}
      </div>
      {ismodeEdit && (
        <button onClick={() => deleteChart(chart.id, chart.widgetId)}>
          <img src={iconDelete} alt="" />
        </button>
      )}
    </div>
  );
}
